// Pending Tailor Actions — cross-item view of alterations to take in and items
// currently at the tailor. Multi-select per section to bulk take-in / mark-back.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Truck, RotateCcw, Square, CheckSquare, type LucideIcon } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, LoadingState, ErrorState, EmptyState, Thumb } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { formatDate } from '@/lib/utils';
import api from '@/lib/api';
import type { TailorAction } from '@/lib/types';

export function WardrobeTailorPage() {
  const { data, loading, error, refetch } = useApi<{ tailorActions: TailorAction[] }>('/tailor-actions');
  const actions = data?.tailorActions ?? [];
  const pending = actions.filter((a) => a.status === 'pending');
  const out = actions.filter((a) => a.status === 'out');

  return (
    <Page>
      <PageHeader section="Tailor Actions" backHref="/wardrobe" backLabel="Wardrobe" />
      <Main className="space-y-6">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && actions.length === 0 && (
          <EmptyState
            icon={<Scissors className="w-8 h-8" />}
            title="Nothing at the tailor"
            description="Add a tailor action from an item to track alterations to take in or bring back."
          />
        )}

        {!loading && !error && (
          <>
            <TailorSection
              title="To Be Tailored"
              actions={pending}
              actionLabel="Take to tailor"
              Icon={Truck}
              endpoint="/tailor-actions/take-to-tailor"
              onDone={refetch}
            />
            <TailorSection
              title="At the tailor"
              actions={out}
              actionLabel="Mark returned"
              Icon={RotateCcw}
              endpoint="/tailor-actions/complete"
              onDone={refetch}
            />
          </>
        )}
      </Main>
    </Page>
  );
}

function TailorSection({
  title,
  actions,
  actionLabel,
  Icon,
  endpoint,
  onDone,
}: {
  title: string;
  actions: TailorAction[];
  actionLabel: string;
  Icon: LucideIcon;
  endpoint: string;
  onDone: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  if (actions.length === 0) return null;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = selected.size === actions.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(actions.map((a) => a.id)));

  const run = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await api.post(endpoint, { ids: Array.from(selected) });
      setSelected(new Set());
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold text-foreground uppercase tracking-wide font-display">
          {title} ({actions.length})
        </h2>
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs font-medium text-primary hover:opacity-80"
        >
          {allSelected ? 'Clear' : 'Select all'}
        </button>
      </div>

      <div className="space-y-2">
        {actions.map((a) => (
          <Card key={a.id} className="p-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggle(a.id)}
              aria-label="Select"
              className="flex-shrink-0"
            >
              {selected.has(a.id) ? (
                <CheckSquare className="w-5 h-5 text-primary" />
              ) : (
                <Square className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            <Thumb url={a.primaryPhotoUrl ?? null} alt="" className="w-12 h-12 rounded-lg flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <Link
                to={`/wardrobe/item/${a.itemId}`}
                className="text-sm font-medium text-foreground truncate block hover:underline"
              >
                {a.itemName}
              </Link>
              <p className="text-xs text-muted-foreground truncate">
                {a.actionType}
                {a.status === 'out' && a.outDate
                  ? ` · sent ${formatDate(a.outDate)}`
                  : a.actionDate
                    ? ` · ${formatDate(a.actionDate)}`
                    : ''}
              </p>
            </div>
          </Card>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="mt-3">
          <Button fullWidth onClick={run} disabled={busy}>
            <Icon className="w-4 h-4" />
            {busy ? 'Working…' : `${actionLabel} (${selected.size})`}
          </Button>
        </div>
      )}
    </section>
  );
}
